/**
 * Convex-backed task data layer.
 *
 * Replaces the Supabase `task-service`. The Zustand store and every component
 * keep working against the existing snake_case `Task` shape, so the swap is
 * confined to this file: Convex documents are translated on the way out and
 * store inputs are translated on the way in.
 *
 * Tasks are single-owner with one optional project link.
 */
import { api } from '@/convex/_generated/api'
import type { Doc } from '@/convex/_generated/dataModel'
import type { Id } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'
import type { Task, CreateTaskDraft } from '@/stores/task-store'
import type { TaskUpdateInput } from '@/lib/task-update-input'
import { toLocalIsoDate } from '@/lib/date-utils'

type ConvexTask = Doc<'tasks'>

const toIsoOrNull = (value: number | undefined) =>
  typeof value === 'number' ? new Date(value).toISOString() : null

const toEpochOrUndefined = (value: string | null | undefined) => {
  if (!value) return undefined
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

export function toStoreTask(doc: ConvexTask): Task {
  const timestamp = new Date(doc._creationTime).toISOString()

  return {
    id: doc._id,
    title: doc.title,
    description: doc.description ?? '',
    priority: doc.priority,
    duration: doc.duration,
    completed: doc.completed,
    archived: doc.archived,
    scheduled: doc.scheduled,
    scheduled_time: doc.scheduledTime ?? null,
    scheduled_date: doc.scheduledDate ?? null,
    scheduled_time_utc: doc.scheduledTimeUtc ?? null,
    deadline_at: toIsoOrNull(doc.deadlineAt),
    reminder_time: doc.reminderTime ?? null,
    created_at: timestamp,
    updated_at: timestamp,
    user_id: doc.clerkUserId,
    individual_project_id: doc.individualProjectId ?? null,
    area_id: doc.areaId ?? null,
    shape: doc.shape,
    recurrence_enabled: doc.recurrenceEnabled,
    recurrence_type: doc.recurrenceType,
    recurrence_interval: doc.recurrenceInterval ?? null,
    recurrence_weekdays: doc.recurrenceWeekdays ?? null,
    recurrence_end_date: doc.recurrenceEndDate ?? null,
    recurrence_anchor: doc.recurrenceAnchor ?? null,
  }
}


/**
 * Whether a caller actually supplied a key, as opposed to leaving it out.
 *
 * Needed for the nullable grouping fields, where `null` ("clear this") and
 * absent ("don't touch this") have to survive as distinct signals.
 */
function hasKey<T extends object>(input: T, key: keyof T): boolean {
  return Object.prototype.hasOwnProperty.call(input, key)
}

/**
 * Reads a field the caller may be clearing.
 *
 * `undefined` means "not supplied, leave it alone" and is stripped by `compact`;
 * `null` means "clear it" and has to survive all the way to the server. Only a
 * key that is present *and* holds a value can carry null through, so a partial
 * update like `{ completed: true }` never blanks a field it never mentioned.
 */
function nullableUpdate<T extends object, K extends keyof T>(
  input: T,
  key: K,
): T[K] | undefined {
  if (!hasKey(input, key)) return undefined
  return input[key] === undefined ? undefined : input[key]
}

/** Strips undefined so Convex arg validation only sees fields actually set. */
function compact<T extends Record<string, unknown>>(input: T) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as T
}

export async function getAllAccessibleTasks(): Promise<Task[]> {
  await whenConvexReady()
  const docs = await convex.query(api.tasks.list, {})
  return docs.map(toStoreTask)
}

export async function getArchivedAccessibleTasks(): Promise<Task[]> {
  await whenConvexReady()
  const docs = await convex.query(api.tasks.list, { includeArchived: true })
  return docs.filter((doc) => doc.archived).map(toStoreTask)
}

export async function getTask(taskId: string): Promise<Task | null> {
  await whenConvexReady()
  const doc = await convex.query(api.tasks.get, { taskId: taskId as Id<'tasks'> })
  return doc ? toStoreTask(doc) : null
}

export async function createTask(draft: CreateTaskDraft): Promise<Task> {
  await whenConvexReady()

  const doc = await convex.mutation(
    api.tasks.create,
    compact({
      title: draft.title,
      description: draft.description,
      priority: draft.priority,
      duration: draft.duration,
      scheduledDate: draft.scheduled_date ?? undefined,
      deadlineAt: toEpochOrUndefined(draft.deadline_at),
      reminderTime: draft.reminder_time ?? undefined,
      individualProjectId: (draft.individual_project_id ?? undefined) as Id<'projects'> | undefined,
      areaId: (draft.area_id ?? undefined) as Id<'areas'> | undefined,
      shape: draft.shape,
      recurrenceEnabled: draft.recurrence_enabled,
      recurrenceType: draft.recurrence_type,
      recurrenceInterval: draft.recurrence_interval ?? undefined,
      recurrenceWeekdays: draft.recurrence_weekdays ?? undefined,
      recurrenceEndDate: draft.recurrence_end_date ?? undefined,
      recurrenceAnchor: draft.recurrence_anchor ?? undefined,
    }),
  )

  return toStoreTask(doc)
}

export async function updateTask(taskId: string, updates: TaskUpdateInput): Promise<Task> {
  await whenConvexReady()

  // Epoch conversion has to happen after the supplied/absent question, not
  // instead of it: an unparseable date and a deliberate `null` both used to
  // arrive here as undefined, making a deadline impossible to remove.
  const deadlineAt =
    nullableUpdate(updates, 'deadline_at') === undefined
      ? undefined
      : (toEpochOrUndefined(updates.deadline_at) ?? null)

  const doc = await convex.mutation(
    api.tasks.update,
    compact({
      taskId: taskId as Id<'tasks'>,
      title: updates.title,
      description: updates.description,
      priority: updates.priority,
      duration: updates.duration,
      completed: updates.completed,
      archived: updates.archived,
      scheduled: updates.scheduled,
      // Absent and null mean different things for every clearable field, so the
      // key's *presence* is what's tested rather than its value. Collapsing null
      // to undefined (as this did before) made "take this task out of its
      // project" impossible, and made unscheduling leave the time behind —
      // `scheduled: false` reached the server while the times were stripped, so
      // the task kept a slot on the day it was no longer planned for.
      // Collapsing absent to null would be worse still, silently blanking these
      // on every partial update like `{ completed: true }`.
      scheduledTime: nullableUpdate(updates, 'scheduled_time'),
      scheduledDate: nullableUpdate(updates, 'scheduled_date'),
      scheduledTimeUtc: nullableUpdate(updates, 'scheduled_time_utc'),
      deadlineAt,
      reminderTime: nullableUpdate(updates, 'reminder_time'),
      individualProjectId: hasKey(updates, 'individual_project_id')
        ? (updates.individual_project_id as Id<'projects'> | null)
        : undefined,
      areaId: hasKey(updates, 'area_id')
        ? (updates.area_id as Id<'areas'> | null)
        : undefined,
      shape: updates.shape,
      recurrenceEnabled: updates.recurrence_enabled,
      recurrenceType: updates.recurrence_type,
      recurrenceInterval: nullableUpdate(updates, 'recurrence_interval'),
      recurrenceWeekdays: nullableUpdate(updates, 'recurrence_weekdays'),
      recurrenceEndDate: nullableUpdate(updates, 'recurrence_end_date'),
      recurrenceAnchor: nullableUpdate(updates, 'recurrence_anchor'),
    }),
  )

  return toStoreTask(doc)
}

export async function deleteTask(taskId: string): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.tasks.remove, { taskId: taskId as Id<'tasks'> })
}

/**
 * Schedules a task at `date`, keeping the display format the UI already
 * expects: `scheduled_time` is a "HH:MM - HH:MM" range derived from the task's
 * duration, and `scheduled_time_utc` is the ISO instant.
 */
export async function scheduleTask(
  taskId: string,
  date: Date,
  recurrence?: {
    recurrence_enabled?: boolean
    recurrence_type?: 'daily' | 'weekly' | 'monthly'
    recurrence_interval?: number | null
    recurrence_weekdays?: number[] | null
    recurrence_anchor?: 'scheduled' | 'completion' | null
    recurrence_end_date?: string | null
    reminder_time?: number | null
    deadline_at?: string | null
  },
): Promise<Task> {
  await whenConvexReady()
  if (!date || Number.isNaN(date.getTime())) {
    throw new Error('Invalid scheduling date')
  }

  const existing = await convex.query(api.tasks.get, { taskId: taskId as Id<'tasks'> })
  if (!existing) throw new Error('Task not found')
  if (existing.completed) throw new Error('Cannot schedule completed tasks')

  const formatTime = (value: Date) =>
    value.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  const endTime = new Date(date)
  endTime.setMinutes(endTime.getMinutes() + existing.duration)

  const updates: Record<string, unknown> = {
    taskId: taskId as Id<'tasks'>,
    scheduled: true,
    scheduledTime: `${formatTime(date)} - ${formatTime(endTime)}`,
    scheduledDate: toLocalIsoDate(date),
    scheduledTimeUtc: date.toISOString(),
  }

  if (recurrence) {
    if (recurrence.deadline_at !== undefined) {
      updates.deadlineAt = toEpochOrUndefined(recurrence.deadline_at)
    }
    if (recurrence.reminder_time !== undefined) {
      updates.reminderTime = recurrence.reminder_time ?? undefined
    }
    if (recurrence.recurrence_enabled !== undefined) {
      updates.recurrenceEnabled = recurrence.recurrence_enabled

      if (recurrence.recurrence_enabled) {
        if (recurrence.recurrence_type !== undefined) updates.recurrenceType = recurrence.recurrence_type
        if (recurrence.recurrence_interval !== undefined)
          updates.recurrenceInterval = recurrence.recurrence_interval ?? undefined
        if (recurrence.recurrence_weekdays !== undefined)
          updates.recurrenceWeekdays = recurrence.recurrence_weekdays ?? undefined
        if (recurrence.recurrence_anchor !== undefined)
          updates.recurrenceAnchor = recurrence.recurrence_anchor ?? undefined
        if (recurrence.recurrence_end_date !== undefined)
          updates.recurrenceEndDate = recurrence.recurrence_end_date ?? undefined
      } else {
        updates.recurrenceEnabled = false
      }
    }
  }

  const doc = await convex.mutation(api.tasks.update, compact(updates) as never)
  return toStoreTask(doc)
}


/**
 * Live task subscription. Convex pushes a fresh list on every relevant write,
 * which replaces the Supabase realtime channel and its polling fallback.
 */
export function subscribeToTasks(
  onTasks: (tasks: Task[]) => void,
  options?: { includeArchived?: boolean },
): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void whenConvexReady().then(() => {
    if (cancelled) return
    const watch = convex.watchQuery(api.tasks.list, {
      includeArchived: options?.includeArchived ?? false,
    })
    unsubscribe = watch.onUpdate(() => {
      const docs = watch.localQueryResult()
      if (docs) onTasks(docs.map(toStoreTask))
    })
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}
